import PropTypes from 'prop-types'
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
} from '@chakra-ui/react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useTranslation} from 'react-i18next'
import {useEffect} from 'react'
import {Alert, DropdownSelect} from '../..'
import {useRails} from '../../../contexts/rails'
import {setExpenseAccount} from '../../../../api'

const validationSchema = Yup.object().shape({
  account: Yup.object().required('Please select an expense account'),
})

const ChooseForm = ({
  data,
  isLoading,
  onSuccess,
  onAccountChange,
  onSubmittingChange,
}) => {
  const {t} = useTranslation('connectExpenseAccount')
  const {organisation, authenticityToken} = useRails()

  const formik = useFormik({
    initialValues: {
      account: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await setExpenseAccount({
          accountId: values.account.value,
          authenticityToken,
          supplierId: organisation.currentSupplier.id,
        })
        onSuccess()
      } catch (e) {
        formik.setFieldError('account', e.message)
      }
    },
  })

  useEffect(() => {
    onSubmittingChange?.(formik.isSubmitting)
  }, [formik.isSubmitting, onSubmittingChange])

  return (
    <form id="choose-expense-account-form" onSubmit={formik.handleSubmit}>
      {formik.errors.account && formik.touched.account && (
        <Alert
          status="error"
          message={
            <Box display="flex" flexDirection="column">
              <Text textStyle="body-small" color="red.500">
                {formik.errors.account}
              </Text>
            </Box>
          }
          mb={8}
        />
      )}

      <Flex flexDirection="column" gap={4}>
        <FormControl
          isInvalid={Boolean(formik.touched.account && formik.errors.account)}
        >
          <FormLabel htmlFor="account">
            <Text fontSize="sm" fontWeight="medium">
              {t('form.fields.choose.label')}
            </Text>
          </FormLabel>

          {isLoading && <Flex mb={6}>Loading...</Flex>}

          {data.length > 0 && (
            <DropdownSelect
              options={data}
              name="expenseAccount"
              placeholder={t('form.fields.choose.placeHolder')}
              variant="mld"
              value={formik.values.account}
              onChange={(value) => {
                formik.setFieldValue('account', value)
                onAccountChange?.(value)
              }}
              onBlur={() => formik.setFieldTouched('account', true)}
            />
          )}
          <FormErrorMessage>{formik.errors.account}</FormErrorMessage>
        </FormControl>
      </Flex>
    </form>
  )
}

ChooseForm.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func,
  onSubmittingChange: PropTypes.func,
}

ChooseForm.defaultProps = {
  isLoading: false,
  onAccountChange: undefined,
  onSubmittingChange: undefined,
}

export default ChooseForm
